import { Link } from 'react-router-dom';
import { Github, Linkedin, Twitter, Youtube, Instagram, Facebook, ArrowUpRight } from 'lucide-react';
import { useFooterLinks, useSiteSettings } from '@/lib/cms';
import { siteSettingsDefault } from '@/lib/site-settings-default';

const SOCIAL_ICONS = {
  linkedin: Linkedin,
  github: Github,
  x: Twitter,
  youtube: Youtube,
  instagram: Instagram,
  tiktok: IconsTikTokFallback,
  facebook: Facebook,
} as const;

function IconsTikTokFallback() {
  // TikTok isn't in lucide-react; fall back to a plain @ glyph.
  return <span className="text-sm font-bold leading-none">@</span>;
}

const COLUMN_LABELS: Record<'company' | 'projects' | 'services' | 'insights', string> = {
  company: 'Company',
  projects: 'Projects',
  services: 'Services',
  insights: 'Insights & More',
};

export function Footer() {
  const { data: settings } = useSiteSettings();
  const { data: footerLinks = [] } = useFooterLinks();

  const s = settings ?? siteSettingsDefault;
  const social = s.social;

  // Group links by column.
  const grouped: Record<'company' | 'projects' | 'services' | 'insights', typeof footerLinks> = {
    company: [],
    projects: [],
    services: [],
    insights: [],
  };
  for (const link of footerLinks) {
    if (link.column in grouped) grouped[link.column].push(link);
  }

  // Fallback columns if no DB rows yet.
  const fallback: Record<keyof typeof grouped, Array<{ label: string; href: string; is_external: boolean }>> = {
    company: [
      { label: 'About', href: '/about', is_external: false },
      { label: 'Team', href: '/team', is_external: false },
      { label: 'Careers', href: '/careers', is_external: false },
      { label: 'Contact', href: '/contact', is_external: false },
      { label: 'Privacy', href: '/legal/privacy', is_external: false },
      { label: 'Terms', href: '/legal/terms', is_external: false },
    ],
    projects: [
      { label: 'All projects', href: '/projects', is_external: false },
      { label: 'Shipped', href: '/projects?status=shipped', is_external: false },
    ],
    services: [
      { label: 'All services', href: '/services', is_external: false },
      { label: 'Custom software', href: '/services#custom-software', is_external: false },
      { label: 'AI solutions', href: '/services#ai', is_external: false },
      { label: 'Cloud', href: '/services#cloud', is_external: false },
    ],
    insights: [
      { label: 'Blog', href: '/blog', is_external: false },
      { label: 'Open roles', href: '/careers', is_external: false },
    ],
  };

  return (
    <footer className="relative mt-32 border-t border-border bg-bg-subtle/30">
      <div aria-hidden className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-gold-500/40 to-transparent" />

      <div className="container-wide py-16 md:py-20">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2.5 transition-opacity hover:opacity-80"
              aria-label={`${s.brand.name} home`}
            >
              <img
                src={s.brand.logo_url ?? '/logo.png'}
                alt=""
                width={256}
                height={256}
                className="h-12 w-12 rounded-md sm:h-14 sm:w-14"
                decoding="async"
              />
              <span className="display-serif text-2xl tracking-tight text-fg sm:text-3xl">
                {s.brand.name}
              </span>
            </Link>
            <p className="mt-4 max-w-sm text-sm text-fg-muted">
              {s.brand.tagline} Headquartered in {s.contact.address_line} — serving ambitious teams across Africa and the world.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {Object.entries(social).map(([key, href]) => {
                if (!href) return null;
                const Icon = SOCIAL_ICONS[key as keyof typeof SOCIAL_ICONS];
                return (
                  <a
                    key={key}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={labelFor(key)}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border text-fg-muted transition-all hover:border-gold-500/40 hover:text-gold-500"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8 md:col-span-8 md:grid-cols-4">
            {(Object.keys(grouped) as Array<keyof typeof grouped>).map((col) => {
              const rows = grouped[col].length > 0 ? grouped[col] : fallback[col];
              return (
                <div key={col}>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-fg">{COLUMN_LABELS[col]}</h3>
                  <ul className="mt-4 space-y-2">
                    {rows.map((link) => (
                      <li key={link.href + link.label}>
                        <Link
                          to={link.href}
                          target={link.is_external ? '_blank' : undefined}
                          rel={link.is_external ? 'noopener noreferrer' : undefined}
                          className="inline-flex items-center text-sm text-fg-muted transition-colors hover:text-fg"
                        >
                          {link.label}
                          {link.is_external && <ArrowUpRight className="ml-1 h-3 w-3 opacity-60" />}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-3 border-t border-border pt-6 text-sm text-fg-subtle sm:mt-16 sm:flex-row sm:items-center sm:justify-between sm:pt-8">
          <p>{s.footer.copyright}</p>
          <p className="flex flex-wrap items-center gap-2">
            <span>To be incorporated</span>
            <ArrowUpRight className="h-3.5 w-3.5" />
            <a href={`mailto:${s.contact.email}`} className="break-all hover:text-fg sm:break-normal">
              {s.contact.email}
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}

function labelFor(key: string): string {
  switch (key) {
    case 'x':
      return 'X';
    case 'linkedin':
      return 'LinkedIn';
    case 'github':
      return 'GitHub';
    case 'youtube':
      return 'YouTube';
    case 'instagram':
      return 'Instagram';
    case 'tiktok':
      return 'TikTok';
    case 'facebook':
      return 'Facebook';
    default:
      return key;
  }
}